import { shortDateTime } from '../../shared/dateTime';
import type { PlanItem } from './vehiclePlanApi';

/**
 * Tabela pod osią harmonogramu auta — stan wiersza i kolejność (czysta logika).
 *
 * ## Stan
 *
 * Omówienie 01.10.2026 (przegląd karty auta przed prezentacją u klienta):
 * kolumna „Stan” ma powiedzieć planiście jedno — czy zlecenie jest już za
 * autem, w drodze, czy przed nim. Stan zlecenia w banku jest na karcie
 * zlecenia; tu liczy się status w TMS klienta (W/P/C/Z — znaczenie ustalone
 * z danych, API: `user-offers/tms-order-status.ts`):
 *
 * - `Z` — zrealizowane;
 * - `C` — w realizacji;
 * - `P`, `W` albo brak statusu — zaplanowane, a wariant (wstępne wskazanie
 *   auta) — wstępnie zaplanowane.
 *
 * Wariant zlecenia, które TMS ma już w realizacji albo zrealizowane, bierze
 * stan z TMS: „wstępnie zaplanowane” przy zleceniu, które już jedzie, byłoby
 * nieprawdą, a taki wariant zostaje aktywny, gdy zlecenie wiezie ktoś spoza
 * naszej floty. Status spoza tej czwórki pokazujemy wprost i nie chowamy —
 * nie zgadujemy, że zlecenie się skończyło.
 *
 * ## Kolejność
 *
 * Najpierw w realizacji, potem zaplanowane i wstępnie zaplanowane od
 * najbliższego załadunku, na końcu zrealizowane od ostatniego. Ze
 * zrealizowanych domyślnie widać tylko ostatnie obsadzenie tego auta (gdzie
 * skończyło pracę); wcześniejsze — i warianty zleceń już zrealizowanych —
 * są pod przyciskiem.
 *
 * Z zaplanowanych (razem z wariantami) domyślnie widać dwa najbliższe —
 * podsumowanie omówienia 01.10.2026: „pokazywać ostatnie zrealizowane,
 * bieżące i ok. 2 zaplanowane”; Mariusz [1:39:43]: „widzisz ostatnie
 * wykonane, to, które wykonujesz, i powiedzmy jeszcze 2, które możesz mieć
 * zaplanowane”. Dalsze są pod przyciskiem, a wszystkie — na osi wyżej.
 * Status spoza W/P/C/Z nie liczy się do limitu i nie chowa się nigdy.
 *
 * ## Trzy linie osi
 *
 * Ten sam podział zasila wykres (omówienie 01.10.2026 [1:33:11]: „3 takie
 * linie — to, które zrealizowaliśmy, to, które realizujemy, i to, które
 * będzie realizowane”): `planPhase` mówi, w której linii stoi pasek.
 * Wstępnie zaplanowane i status spoza W/P/C/Z idą do „zaplanowanych” — nie
 * zgadujemy, że zlecenie już jedzie albo się skończyło.
 */

/** Ile zaplanowanych (z wariantami) tabela pokazuje bez rozwijania. */
export const PLANNED_SHOWN = 2;

export type PlanPhase = 'done' | 'inProgress' | 'planned';

/** Linie osi w kolejności z góry na dół — od przeszłości do przyszłości. */
export const PLAN_PHASES: ReadonlyArray<{ phase: PlanPhase; label: string }> = [
  { phase: 'done', label: 'Zrealizowane' },
  { phase: 'inProgress', label: 'W realizacji' },
  { phase: 'planned', label: 'Zaplanowane' },
];

export type PlanRowState = 'inProgress' | 'planned' | 'tentative' | 'done' | 'unknown';

export interface PlanRowStatus {
  state: PlanRowState;
  /** Tekst w kolumnie „Stan”. */
  label: string;
}

/**
 * „Status w TMS: obsadzone (P)” — najpierw opis, litera z TMS w nawiasie
 * (poprawa brzmienia 01.10.2026: wcześniej „P — obsadzone”). Litera zostaje,
 * bo planista zna ją z TMS. Bez opisu — sama litera; bez statusu — `null`.
 * Wspólne dla dymku paska na osi i stanu w tabeli.
 */
export function tmsStatusText(item: Pick<PlanItem, 'tmsOrderStatus' | 'tmsOrderStatusLabel'>): string | null {
  if (!item.tmsOrderStatus) return null;
  return item.tmsOrderStatusLabel
    ? `Status w TMS: ${item.tmsOrderStatusLabel} (${item.tmsOrderStatus})`
    : `Status w TMS: ${item.tmsOrderStatus}`;
}

export function planRowStatus(item: Pick<PlanItem, 'kind' | 'tmsOrderStatus'>): PlanRowStatus {
  const code = item.tmsOrderStatus;
  if (code === 'Z') return { state: 'done', label: 'zrealizowane' };
  if (code === 'C') return { state: 'inProgress', label: 'w realizacji' };
  if (code === 'P' || code === 'W' || code === null || code === undefined || code === '') {
    return item.kind === 'candidate'
      ? { state: 'tentative', label: 'wstępnie zaplanowane' }
      : { state: 'planned', label: 'zaplanowane' };
  }
  return { state: 'unknown', label: `status w TMS: ${code}` };
}

/**
 * Kandydatura do zlecenia, które TMS ma w realizacji (`C`) albo zrealizowane
 * (`Z`) — od 24.10.2026 (S-24.10-06, `runningOrderNotVariant`; decyzja
 * użytkownika 21.10.2026, C33: „zlecenie w realizacji w TMS nie jest
 * wariantem”) to nie wariant: nie stoi w wierszu „Warianty”, nie liczy się
 * do liczby wariantów i ma podpis w tabeli. Kandydatury nie zwalniamy.
 */
export function runningInTms(item: Pick<PlanItem, 'kind' | 'tmsOrderStatus'>): boolean {
  return item.kind === 'candidate' && (item.tmsOrderStatus === 'C' || item.tmsOrderStatus === 'Z');
}

/** Podpis w kolumnie „Stan” dla kandydatury do zlecenia w realizacji (S-24.10-06). */
export function notVariantLabel(item: Pick<PlanItem, 'tmsOrderStatus'>): string {
  return item.tmsOrderStatus === 'Z'
    ? 'zlecenie zrealizowane w TMS — nie jest wariantem'
    : 'zlecenie w realizacji w TMS — nie jest wariantem';
}

/** Linia osi, w której stoi pasek pozycji — z tego samego stanu co kolumna „Stan”. */
export function planPhase(item: Pick<PlanItem, 'kind' | 'tmsOrderStatus'>): PlanPhase {
  const { state } = planRowStatus(item);
  if (state === 'done') return 'done';
  if (state === 'inProgress') return 'inProgress';
  return 'planned';
}

/**
 * Zaplanowane, choć okno zlecenia już minęło (UWAGI.md, pytanie do Mariusza
 * o PL/R/26/10/052494): TMS nie zamknął zlecenia — stare „P” (pytanie 41 do
 * Euvic). Pokazujemy jako zaplanowane z dopiskiem „po oknie”, nie zgadujemy,
 * że się skończyło. Wspólne dla tabeli pod osią i panelu „Do zrobienia”.
 */
export function pastWindow(item: Pick<PlanItem, 'kind' | 'tmsOrderStatus' | 'windowTo'>, now: Date): boolean {
  const { state } = planRowStatus(item);
  if (state !== 'planned' && state !== 'tentative') return false;
  if (!item.windowTo) return false;
  return new Date(item.windowTo).getTime() < now.getTime();
}

/** Początek wiersza — ten sam, co w kolumnie „Załadunek”; brak albo zła data — `null`. */
function startOf(item: PlanItem): number | null {
  const at = item.loadingFrom ?? item.windowFrom ?? item.occupancyFrom;
  if (!at) return null;
  const ms = new Date(at).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/** Rosnąco albo malejąco po początku; wiersz bez daty zawsze na końcu. */
function byStart(direction: 1 | -1) {
  return (a: PlanItem, b: PlanItem): number => {
    const sa = startOf(a);
    const sb = startOf(b);
    if (sa === null || sb === null) {
      if (sa !== sb) return sa === null ? 1 : -1;
    } else if (sa !== sb) {
      return (sa - sb) * direction;
    }
    return (a.orderId - b.orderId) * direction;
  };
}

export interface ArrangedPlanRows {
  /** Wiersze tabeli w kolejności wyświetlania. */
  rows: PlanItem[];
  /** Ile zrealizowanych chowa widok domyślny — liczba na przycisku. */
  hiddenDone: number;
  /** Ile dalszych zaplanowanych (z wariantami) chowa widok domyślny. */
  hiddenPlanned: number;
}

/**
 * Wiersze tabeli planu. `showEarlierDone` — po rozwinięciu wszystkie
 * zrealizowane, od ostatniego; domyślnie tylko ostatnie zrealizowane
 * obsadzenie. `showAllPlanned` — wszystkie zaplanowane; domyślnie
 * `PLANNED_SHOWN` najbliższych.
 */
export function arrangePlanRows(
  items: readonly PlanItem[],
  showEarlierDone = false,
  showAllPlanned = false,
): ArrangedPlanRows {
  const isDone = (i: PlanItem) => planRowStatus(i).state === 'done';
  const isPlanned = (i: PlanItem) => {
    const { state } = planRowStatus(i);
    return state === 'planned' || state === 'tentative';
  };
  const rank = (i: PlanItem) => (planRowStatus(i).state === 'inProgress' ? 0 : 1);
  const startsFirst = byStart(1);
  const current = items
    .filter((i) => !isDone(i))
    .sort((a, b) => rank(a) - rank(b) || startsFirst(a, b));
  // Najbliższe zaplanowane to pierwsze w kolejności `current` (od załadunku).
  const planned = current.filter(isPlanned);
  const hiddenPlanned = showAllPlanned ? [] : planned.slice(PLANNED_SHOWN);
  const shownCurrent = current.filter((i) => !hiddenPlanned.includes(i));
  const done = items.filter(isDone).sort(byStart(-1));
  const latest = done.find((i) => i.kind === 'assignment');
  const shownDone = showEarlierDone ? done : latest ? [latest] : [];
  return {
    rows: [...shownCurrent, ...shownDone],
    hiddenDone: done.length - (latest ? 1 : 0),
    hiddenPlanned: Math.max(0, planned.length - PLANNED_SHOWN),
  };
}

/** „30.09, 08:00” — data i godzina nie rozdzielają się przy zawijaniu (spacja nierozdzielająca). */
const stamp = (iso: string) => shortDateTime(iso).replace(' ', '\u00a0');

/**
 * Przedział w tabeli pod osią: „30.09, 08:00 – 30.09, 12:00”. Bez końca —
 * „od …”, bez początku — „do …”, bez obu — kreska (06.10.2026: pozycje bez
 * godziny końca okna pokazywały „06.10, 05:13 – —”). Między datą a godziną
 * spacja nierozdzielająca: w wąskiej kolumnie przedział łamie się przy
 * myślniku, a nie w środku chwili.
 */
export function rangeText(from: string | null | undefined, to: string | null | undefined): string {
  if (!from && !to) return '—';
  if (!to) return `od ${stamp(from as string)}`;
  if (!from) return `do ${stamp(to)}`;
  return `${stamp(from)} – ${stamp(to)}`;
}
